import React, { PureComponent } from "react";
import Flexbox from "flexbox-react";
import MediaQuery from "react-responsive";
import _ from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { createPortal } from "react-dom";
import ErrorBoundary from "../errors/errorBoundary";

import Alerts from "../../components/app-alerts.js";
import NavLinks from "../navPanel/app-nav-links";
import NavPanel from "../navPanel/app-nav-panel";
import { SaveModal } from "../drive/";
import ChecklistPreviewModal from "../app-checklist-preview-modal";
import AiAssistant from "../app-ai-assistant";
import { getLs, isAiAssistantEnabled, setLs } from "../../utils/utils";
import { AI_ASSISTANT_LAUNCH_EVENT } from "../../ai/assistantLaunch";
import state from "../../state/state";

export default class Page extends PureComponent {
	constructor(props) {
		super(props);
		const aiAssistantEnabled = this.isAiAssistantGloballyEnabled(props);
		const storedAiAssistantMode = getLs("globalAiAssistantMode");
		const aiAssistantMode = storedAiAssistantMode === "overlay" || storedAiAssistantMode === "side" ? storedAiAssistantMode : "overlay";
		this.state = {
			showAiAssistant: aiAssistantEnabled && getLs("globalAiAssistantOpen") === true,
			aiAssistantMode
		};
		this.handleAiAssistantLaunchEvent = this.handleAiAssistantLaunchEvent.bind(this);
	}

	getAiAssistantUrlParams(props = this.props) {
		if (props && props.urlParams && typeof props.urlParams === "object") {
			return props.urlParams;
		}
		const stateUrlParams = state.get(["appState", "urlParams"]);
		return stateUrlParams && typeof stateUrlParams === "object" ? stateUrlParams : {};
	}

	isAiAssistantGloballyEnabled(props = this.props) {
		return isAiAssistantEnabled(this.getAiAssistantUrlParams(props));
	}

	openAiAssistantPanel() {
		if (!this.isAiAssistantGloballyEnabled()) {
			return;
		}
		setLs("globalAiAssistantOpen", true);
		this.setState({
			showAiAssistant: true
		});
	}

	closeAiAssistantPanel() {
		setLs("globalAiAssistantOpen", false);
		this.setState({
			showAiAssistant: false
		});
	}

	toggleAiAssistantMode() {
		if (!this.isAiAssistantGloballyEnabled()) {
			return;
		}
		const nextMode = this.state.aiAssistantMode === "overlay" ? "side" : "overlay";
		setLs("globalAiAssistantMode", nextMode);
		this.setState({
			aiAssistantMode: nextMode
		});
	}

	handleAiAssistantLaunchEvent(event) {
		if (!this.isAiAssistantGloballyEnabled()) {
			return;
		}
		const detail = event && event.detail ? event.detail : {};
		const preferredMode = detail.preferredPanelMode;
		if (preferredMode === "overlay" || preferredMode === "side") {
			setLs("globalAiAssistantMode", preferredMode);
			this.setState({
				aiAssistantMode: preferredMode
			});
		}
		this.openAiAssistantPanel();
	}

	componentDidMount() {
		if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
			window.addEventListener(AI_ASSISTANT_LAUNCH_EVENT, this.handleAiAssistantLaunchEvent);
		}
	}

	componentDidUpdate(prevProps) {
		const wasEnabled = this.isAiAssistantGloballyEnabled(prevProps);
		const isEnabled = this.isAiAssistantGloballyEnabled(this.props);
		if (wasEnabled !== isEnabled && !isEnabled && this.state.showAiAssistant) {
			this.closeAiAssistantPanel();
		}
	}

	componentWillUnmount() {
		if (typeof window !== "undefined" && typeof window.removeEventListener === "function") {
			window.removeEventListener(AI_ASSISTANT_LAUNCH_EVENT, this.handleAiAssistantLaunchEvent);
		}
	}

	render() {

		let contentAreaStyles = { overflowY: "auto", width: "100%", backgroundColor: "white" };
		if (this.props.contentAreaStyles) {
			contentAreaStyles = _.assign({}, contentAreaStyles, this.props.contentAreaStyles);
		}

		const classNames = ["pageWrapper"];
		if (this.props.className) {
			classNames.push(this.props.className);
		}

		const { title, subTitle, toolbar, flexLayout } = this.props;
		let titleComponent = null;
		if (title) {
			titleComponent = <div className="title">
				<h3 style={{ marginTop: "10px", marginBottom: 0 }}>{title}</h3>
				{subTitle && <h5 style={{ margin: 0, marginTop: "10px", color: "darkgray" }}>{subTitle}</h5>}
			</div>;
		}
		const pageHeader = titleComponent || toolbar ? <div className={`page-header${flexLayout ? " flex" : ""}`}>
			{titleComponent}
			{toolbar && <div>
				{toolbar}
			</div>}
		</div> : null;

		const isNarrow = typeof window !== "undefined" && window.innerWidth < 1024;
		const overlayMode = this.state.aiAssistantMode === "overlay";
		const panelWidth = overlayMode ? (isNarrow ? "calc(100vw - 20px)" : "420px") : (isNarrow ? "340px" : "420px");
		const portalTarget = typeof document !== "undefined" ? document.body : null;
		const aiAssistantEnabled = this.isAiAssistantGloballyEnabled();
		const aiPanelActionStyle = {
			cursor: "pointer",
			color: "#e6efff",
			backgroundColor: "#18263a",
			border: "1px solid #4b6d95",
			borderRadius: "6px",
			padding: "0px 8px",
			lineHeight: "20px",
			minWidth: "24px",
			fontWeight: 700,
			textAlign: "center"
		};
		const modeToggleIcon = this.state.aiAssistantMode === "overlay" ? "columns" : "clone";
		const modeToggleTitle = this.state.aiAssistantMode === "overlay" ? "Switch to side panel mode" : "Switch to floating overlay mode";
		const renderAiAssistantPanel = ({ overlay = false } = {}) => {
			return (
				<div style={overlay ? {
					position: "fixed",
					right: "10px",
					bottom: "18px",
					height: "min(70vh, 760px)",
					maxHeight: "calc(100vh - 120px)",
					width: panelWidth,
					minWidth: isNarrow ? "280px" : "320px",
					maxWidth: "calc(100vw - 20px)",
					zIndex: 1000000,
					display: "flex",
					flexDirection: "column",
					border: "1px solid #263246",
					borderRadius: "12px",
					overflow: "hidden",
					background: "linear-gradient(180deg, #111723 0%, #0c1018 100%)",
					boxShadow: "0 12px 30px rgba(5, 9, 15, 0.35)",
					transition: "width 0.16s ease, height 0.16s ease"
				} : {
					display: "flex",
					flexDirection: "column",
					flex: "0 0 auto",
					width: panelWidth,
					minWidth: isNarrow ? "280px" : "320px",
					maxWidth: "46vw",
					minHeight: 0,
					height: "100%",
					borderLeft: "1px solid #263246",
					background: "linear-gradient(180deg, #111723 0%, #0c1018 100%)",
					overflow: "hidden",
					transition: "width 0.16s ease"
				}}>
					<div style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						gap: "6px",
						padding: "10px 12px",
						background: "linear-gradient(180deg, #202c3d 0%, #192536 100%)",
						borderBottom: "1px solid #2e2e2e",
						minHeight: "44px"
					}}>
						<div style={{ fontWeight: 700, letterSpacing: "0.2px", color: "white" }}>AI Assistant</div>
						<div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
							<div
								style={{ ...aiPanelActionStyle, minWidth: "40px" }}
								title={modeToggleTitle}
								onClick={() => this.toggleAiAssistantMode()}
							>
								<FontAwesomeIcon icon={modeToggleIcon} />
							</div>
							<div
								style={aiPanelActionStyle}
								title="Close AI Assistant"
								onClick={() => this.closeAiAssistantPanel()}
							>
								<FontAwesomeIcon icon="times" />
							</div>
						</div>
					</div>
					<div style={{
						flex: 1,
						minHeight: 0,
						padding: "10px",
						overflow: "hidden",
						display: "flex"
					}}>
						<div style={{ flex: 1, minHeight: 0, backgroundColor: "transparent" }}>
							<AiAssistant allowActivityMode={false} allowFilesMode={false} />
						</div>
					</div>
				</div>
			);
		};
		return (

			<>
				<Flexbox className={classNames.join(" ")} flexDirection="column">
					<Alerts />
					<SaveModal />
					{/* <NavLinks /> */}
					<NavLinks showReorder={this.props.showReorder} onReorder={this.props.onReorder} />
					{/* <MediaQuery query='(max-width: 599px)'>
						<Flexbox flex="1" flexDirection="row" width="100vw" style={{ minHeight: 0 }}>
							<Flexbox style={contentAreaStyles} padding="10px" flexDirection="column" flexGrow={1} alignItems="center" justifyContent="flex-start">
								{this.props.children}
							</Flexbox>
						</Flexbox>
					</MediaQuery>
					<MediaQuery query='(min-width: 600px)'> */}
					<Flexbox flex="1" flexDirection="row" width="100vw" style={{ minHeight: 0 }}>
						<MediaQuery query='(min-width: 768px)'>
							<NavPanel onFilter={this.props.onFilter} onFilterEnd={this.props.onFilterEnd} />
						</MediaQuery>
						<Flexbox flex="1" flexDirection="row" style={{ minHeight: 0, minWidth: 0, overflow: "hidden" }}>
							<Flexbox style={{ ...contentAreaStyles, minWidth: 0, width: this.state.showAiAssistant && this.state.aiAssistantMode === "side" ? "auto" : contentAreaStyles.width }} padding={this.props.padding ? this.props.padding : "10px"} flexDirection="column" flex="1" justifyContent="flex-start" className="page-scroll-view">
								<ErrorBoundary>
									{pageHeader}
									{this.props.children}
								</ErrorBoundary>
							</Flexbox>
							{aiAssistantEnabled && this.state.showAiAssistant && this.state.aiAssistantMode === "side" && renderAiAssistantPanel({ overlay: false })}
						</Flexbox>
					</Flexbox>
					<ChecklistPreviewModal />
					{/* </MediaQuery> */}
				</Flexbox>

					{aiAssistantEnabled && portalTarget && createPortal(
						<>
							{!this.state.showAiAssistant &&
							<div
								onClick={() => this.openAiAssistantPanel()}
								style={{
									position: "fixed",
									right: "16px",
									bottom: "18px",
									zIndex: 1000000,
									width: "50px",
									minWidth: "50px",
									height: "50px",
									borderRadius: "999px",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									padding: 0,
									color: "white",
									cursor: "pointer",
									border: "1px solid #4b6d95",
									background: "linear-gradient(180deg, #273953 0%, #16243a 100%)",
									boxShadow: "0 10px 25px rgba(5, 9, 15, 0.35)"
								}}
								title="Open AI Assistant"
							>
								<FontAwesomeIcon icon="comment-alt" />
							</div>
						}

							{this.state.showAiAssistant && this.state.aiAssistantMode === "overlay" && renderAiAssistantPanel({ overlay: true })}
						</>,
						portalTarget
					)}
			</>
		);
	}
}
